import {
  type ApprovalChatRequest,
  type ApprovalDecisionResponse,
  ApprovalDecisionResponseSchema,
  type ApprovalProposalDetailResponse,
  ApprovalProposalDetailResponseSchema,
  type ApprovalProposalListParams,
  ApprovalProposalListParamsSchema,
  type ApprovalProposalListResponse,
  ApprovalProposalListResponseSchema,
  type ApprovalSubmitRequest,
  type BulkApprovalRequest,
  type BulkApprovalResponse,
  BulkApprovalResponseSchema,
} from "@adpilot/shared";
import { assertAiOperationsMockWritable, isAiOperationsMockEnabled } from "@/mocks/enabled";
import { api } from "./api";

const idempotencyHeaders = (idempotencyKey: string) => ({
  "Idempotency-Key": idempotencyKey,
});

// 审批 API 是单店作用域(X-Shop-Id)。运行详情页跟随所访问资源的店铺,在聚合
// 视角下与当前激活店铺可能不同,因此这些函数接受显式 shopId 覆盖默认头
// (api.ts 仅在未显式设置时补当前店铺头)。
const shopHeaders = (shopId?: string) => (shopId ? { "X-Shop-Id": shopId } : {});

function listSearchParams(input: ApprovalProposalListParams): Record<string, string | number> {
  const params = ApprovalProposalListParamsSchema.parse(input);
  return Object.fromEntries(
    Object.entries(params).filter((entry): entry is [string, string | number] => {
      const value = entry[1];
      return typeof value === "string" || typeof value === "number";
    }),
  );
}

export async function fetchApprovalProposals(
  params: ApprovalProposalListParams,
): Promise<ApprovalProposalListResponse> {
  // 内联 DEV 字面量让 Rollup 在生产构建里整段裁剪，避免 mock chunk 被打进产物；
  // 跨模块的 isAiOperationsMockEnabled() 调用无法被常量折叠。
  if (import.meta.env.DEV && isAiOperationsMockEnabled()) {
    const parsed = ApprovalProposalListParamsSchema.parse(params);
    const { getAiOperationsMockApprovalList } = await import("@/mocks/ai-operations");
    return getAiOperationsMockApprovalList(parsed.page, parsed.pageSize);
  }
  const response = await api
    .get("approvals", { searchParams: listSearchParams(params) })
    .json<unknown>();
  return ApprovalProposalListResponseSchema.parse(response);
}

export async function fetchApprovalProposalDetail(
  proposalId: string,
  shopId?: string,
): Promise<ApprovalProposalDetailResponse> {
  if (import.meta.env.DEV && isAiOperationsMockEnabled()) {
    const { getAiOperationsMockProposalDetail } = await import("@/mocks/ai-operations");
    return getAiOperationsMockProposalDetail(proposalId);
  }
  const response = await api
    .get(`proposals/${proposalId}`, { headers: shopHeaders(shopId) })
    .json<unknown>();
  return ApprovalProposalDetailResponseSchema.parse(response);
}

export async function submitApprovalProposal(
  proposalId: string,
  body: ApprovalSubmitRequest,
  idempotencyKey: string,
  shopId?: string,
): Promise<ApprovalDecisionResponse> {
  assertAiOperationsMockWritable();
  const response = await api
    .post(`proposals/${proposalId}/review-submissions`, {
      json: body,
      headers: { ...idempotencyHeaders(idempotencyKey), ...shopHeaders(shopId) },
    })
    .json<unknown>();
  return ApprovalDecisionResponseSchema.parse(response);
}

export async function bulkApproveProposals(
  body: BulkApprovalRequest,
  idempotencyKey: string,
): Promise<BulkApprovalResponse> {
  assertAiOperationsMockWritable();
  const response = await api
    .post("approvals/bulk-approve", {
      json: body,
      headers: idempotencyHeaders(idempotencyKey),
    })
    .json<unknown>();
  return BulkApprovalResponseSchema.parse(response);
}

export async function requestProposalRevision(
  proposalId: string,
  body: ApprovalChatRequest,
  idempotencyKey: string,
  shopId?: string,
): Promise<ApprovalDecisionResponse> {
  assertAiOperationsMockWritable();
  const response = await api
    .post(`proposals/${proposalId}/revisions`, {
      json: body,
      headers: { ...idempotencyHeaders(idempotencyKey), ...shopHeaders(shopId) },
    })
    .json<unknown>();
  return ApprovalDecisionResponseSchema.parse(response);
}
